import { memo, useContext, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import { useResizeDetector } from 'react-resize-detector'

import { Box, Divider, Typography } from '@mui/material'
import { ControlledCheckbox, Spinner } from '@rent/ui'
import { DownloadFileButton, TextLink } from 'components/shared'
import { ExternalLinks } from 'consts'
import { NotificationsContext, OrderContext, notifyError } from 'context'
import { ContractGeneratedDataFields, IAgreementResponse } from 'models'

import { contractContainerBoxStyle, contractContainerTitleBoxStyle } from './styles'

const url = `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`
pdfjs.GlobalWorkerOptions.workerSrc = url

const maxDocumentWidth = 650

interface ContractViewProps extends IAgreementResponse {
  onLoadError: () => void
}

const ContractView = memo(({ frameworkAgreementExposedId, leaseAgreementExposedId, pdf, onLoadError }: ContractViewProps) => {
  const base64Pdf = `data:application/pdf;base64,${pdf}`
  const { formatMessage } = useIntl()
  const [numPages, setNumPages] = useState<number>()
  const { width: containerWidth, ref } = useResizeDetector()
  const { dispatchNotification } = useContext(NotificationsContext)
  const { contextData } = useContext(OrderContext)

  const { control } = useFormContext()

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    // scroll to the document position
    if (ref?.current) {
      window.scroll(0, ref?.current.offsetTop - 180)
      ref.current.focus()
    } else {
      window.scroll(0, 0)
    }
    setNumPages(numPages)
  }

  const onDownloadAgreement = () => {
    const linkSource = base64Pdf
    const downloadLink = document.createElement('a')
    const fileName = 'umowaWeSub.pdf'

    downloadLink.href = linkSource
    downloadLink.download = fileName
    downloadLink.click()
  }

  const onDocumentLoadError = () => {
    notifyError(dispatchNotification, {
      title: formatMessage({ id: 'notification.dear_customer' }),
      content: formatMessage({ id: 'notification.pdf_loading_error' }),
    })
    onLoadError()
  }

  return (
    <Box>
      <Box sx={contractContainerBoxStyle}>
        <Box sx={contractContainerTitleBoxStyle}>
          <Typography variant="h5" mb={{ xs: 1, sm: 0 }}>
            {formatMessage({ id: 'notification.read_the_agreement' })}
          </Typography>
          <DownloadFileButton text="button.download_agreement" onClick={onDownloadAgreement} id="downloadAgreement-button" />
        </Box>
        <Divider />
        <Box
          sx={{
            height: 472,
            overflow: 'auto',
            scrollbarWidth: 'thin',
            my: 2.4,
          }}
          ref={ref}
          tabIndex={0}
        >
          <Document
            file={base64Pdf}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <Box sx={{ height: 472, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner />
              </Box>
            }
            error={<Typography sx={{ p: 1.6 }}>{formatMessage({ id: 'notification.pdf_loading_error' })}</Typography>}
          >
            {Array.from(Array(numPages).keys()).map(el => (
              <Page key={el + 1} pageNumber={el + 1} width={containerWidth ? containerWidth - 10 : maxDocumentWidth} />
            ))}
          </Document>
        </Box>
      </Box>
      <Box mt={1.6} mb={{ xs: 3.2, md: 6 }}>
        <ControlledCheckbox
          fieldName={ContractGeneratedDataFields.agreementAcceptance}
          label={formatMessage({ id: contextData.isLoan ? 'form.agreementAcceptanceLoan_label' : 'form.agreementAcceptance_label' })}
          required
          control={control}
          type="checkbox-accordion"
          labelExpanded={formatMessage(
            { id: contextData.isLoan ? 'form.agreementAcceptanceLoan_label_expanded' : 'form.agreementAcceptance_label_expanded' },
            {
              frameworkAgreementExposedId,
              leaseAgreementExposedId,
              art: (
                <>
                  77<sup>2</sup>
                </>
              ),
              link1: (
                <TextLink href={contextData.isLoan ? ExternalLinks.LOAN_DOCUMENTS : ExternalLinks.DOCUMENTS} id="wesubDocuments-link">
                  {formatMessage({ id: contextData.isLoan ? 'form.loan_of_terms' : 'form.of_terms_and_toip' })}
                </TextLink>
              ),
            },
          )}
        />
        {contextData.isLoan && (
          <ControlledCheckbox
            fieldName={ContractGeneratedDataFields.nettingAcceptance}
            label={formatMessage({ id: 'form.nettingAcceptance_label' })}
            required
            control={control}
            type="checkbox-accordion"
            labelExpanded={formatMessage(
              { id: 'form.nettingAcceptance_label_expanded' },
              {
                leaseAgreementExposedId,
                art: (
                  <>
                    77<sup>2</sup>
                  </>
                ),
              },
            )}
          />
        )}
      </Box>
    </Box>
  )
})

export default ContractView
